import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Layout from '../components/Layout';
import { useI18n } from '../i18n';
import { useCan } from '../hooks/useMe';
import api from '../services/api';
import GeneralQuoteHelper from '../quotes/catalog/format';
import { downloadQuotePdf } from '../quotes/pdf/downloadQuote';

const VIEWS = ['all', 'mine', 'renewals', 'newClients'];

// El proyecto no trae @mui/icons-material, así que el bote va como SVG en
// línea y hereda el color del botón que lo contiene.
function TrashIcon() {
  return (
    <svg
      width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

// Renovación en plum, como el tier Enterprise: es el negocio que ya existe.
const RENEWAL_STYLE = { bg: '#F3E9F3', fg: '#531253' };
const NEW_STYLE = { bg: '#EDE6E3', fg: '#6B5F68' };

export default function Quotes() {
  const navigate = useNavigate();
  const { t, lang, fmtDate } = useI18n();
  const canEdit = useCan('edit_quotes');
  const canDelete = useCan('delete_quotes');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState('all');
  const [q, setQ] = useState('');

  const params = useMemo(() => {
    if (view === 'mine') return { owner: 'me' };
    if (view === 'renewals') return { renewal: true };
    if (view === 'newClients') return { renewal: false };
    return {};
  }, [view]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/quotes', { params: { ...params, q: q || undefined } })
      .then((res) => { setRows(res.data.quotes); setTotal(res.data.total); setFailed(false); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [params, q]);

  useEffect(() => {
    const id = setTimeout(load, q ? 300 : 0);   // pequeña espera al escribir
    return () => clearTimeout(id);
  }, [load, q]);

  async function remove(quote) {
    if (!window.confirm(t('quote.deleteConfirm'))) return;
    try {
      await api.delete(`/api/quotes/${quote.quote_id}`);
      load();
    } catch {
      setFailed(true);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'q_folio', headerName: t('quote.folio'), width: 90,
      renderCell: ({ value }) => <Box sx={{ fontWeight: 500 }}>#{value}</Box>,
    },
    {
      field: 'company', headerName: t('quote.company'), flex: 2, minWidth: 220,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.8, lineHeight: 1.4 }}>
          <Box sx={{ fontWeight: 500 }}>{row.company || '—'}</Box>
          <Box sx={{ fontSize: 11.5, color: '#847782' }}>
            {row.contact_name ? `${row.contact_name} · ` : ''}{row.email}
          </Box>
          {row.tenant_id ? (
            <Box sx={{ fontSize: 11.5, color: '#531253' }}>
              ● {row.account_name || row.tenant_id}
            </Box>
          ) : (
            <Box sx={{ fontSize: 11.5, color: '#847782' }}>○ {t('quote.notLinked')}</Box>
          )}
        </Box>
      ),
    },
    {
      field: 'total', headerName: t('quote.total'), width: 140, align: 'right', headerAlign: 'right',
      renderCell: ({ row }) => (
        <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
          {GeneralQuoteHelper.getCurrencyFormat(row.total || 0, row.currency || 'MXN')}
        </Box>
      ),
    },
    { field: 'currency', headerName: t('quote.currency'), width: 90 },
    {
      field: 'is_renewal', headerName: t('quote.type'), width: 140,
      renderCell: ({ value }) => {
        const style = value ? RENEWAL_STYLE : NEW_STYLE;
        return (
          <Chip
            size="small"
            label={value ? t('quote.isRenewal') : t('quote.newClient')}
            sx={{
              backgroundColor: style.bg, color: style.fg, fontWeight: 600,
              fontSize: 10, letterSpacing: '0.06em', height: 20, borderRadius: '3px',
            }}
          />
        );
      },
    },
    { field: 'owner_name', headerName: t('accounts.columns.salesOwner'), width: 140,
      renderCell: ({ value }) => value || <Box sx={{ color: '#847782' }}>—</Box> },
    {
      field: 'createdAt', headerName: t('quote.createdDate'), width: 130, align: 'right', headerAlign: 'right',
      renderCell: ({ value }) => fmtDate(value),
    },
    {
      field: 'pdf', headerName: '', width: 60, sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" title={t('quote.downloadPdf')}
          onClick={(e) => { e.stopPropagation(); downloadQuotePdf(row, { onError: () => setFailed(true) }); }}
          sx={{ fontSize: 11.5, color: '#531253', minWidth: 0 }}>PDF</Button>
      ),
    },
    ...(canDelete ? [{
      field: 'actions', headerName: '', width: 60, sortable: false,
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          aria-label={t('common.delete')}
          title={t('common.delete')}
          onClick={(e) => { e.stopPropagation(); remove(row); }}
          sx={{ color: '#AE5B61', '&:hover': { backgroundColor: '#FFF0F0' } }}
        >
          <TrashIcon />
        </IconButton>
      ),
    }] : []),
  ], [lang, canDelete]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        {VIEWS.map((v) => (
          <Chip
            key={v} size="small" label={t(`quote.views.${v}`)}
            onClick={() => setView(v)}
            sx={{
              fontSize: 12.5, borderRadius: '14px',
              backgroundColor: view === v ? '#531253' : '#FFFFFF',
              color: view === v ? '#fff' : '#847782',
              border: '1px solid #E4E0E2', fontWeight: view === v ? 500 : 400,
            }}
          />
        ))}
        <TextField
          size="small" placeholder={`${t('quote.company')}…`}
          value={q} onChange={(e) => setQ(e.target.value)}
          sx={{ ml: 'auto', minWidth: 220, backgroundColor: '#fff' }}
        />
        {canEdit && (
          <Button variant="contained" size="small" onClick={() => navigate('/quotes/new')}>
            {t('quote.newQuote')}
          </Button>
        )}
      </Box>

      {failed && <Alert severity="error" sx={{ mb: 2 }}>{t('quote.loadError')}</Alert>}

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.quote_id}
        loading={loading}
        autoHeight
        rowHeight={64}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        onRowClick={({ row }) => navigate(`/quotes/${row.quote_id}`)}
        sx={{
          backgroundColor: '#fff', cursor: 'pointer', border: '1px solid #E4E0E2',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: 11, fontWeight: 600, letterSpacing: '0.07em',
            textTransform: 'uppercase', color: '#847782',
          },
          '& .MuiDataGrid-cell': { borderBottom: '1px solid #F0ECEE' },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#FFF0F0' },
        }}
      />
      <Box sx={{ mt: 1, fontSize: 12, color: '#847782' }}>{total} {t('quote.quotes').toLowerCase()}</Box>
    </Layout>
  );
}
